// # Bank Logs Component
import React, { useState } from 'react';
import './bnct_bank_logs.css';

const BankLogs = ({ revenueData, member }) => {
  // # State for log filtering
  const [logType, setLogType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  
  // # Check if data is available
  if (!revenueData) return <div className="bnct_loading">Loading logs data...</div>;
  
  // # Determine if this is a project budget account
  const isProjectBudget = member && member.id === 'project-budget';
  
  // # Get logs based on account type
  const paymentLogs = isProjectBudget ? 
    (revenueData.budgetAllocationLogs || []) : 
    (revenueData.paymentLogs || []);
  
  const expenseLogs = isProjectBudget ? 
    (revenueData.expenseLogs || []) : 
    (revenueData.salaryLogs || []);
  
  // # Filter and sort logs
  const filteredLogs = getFilteredLogs();
  
  return (
    <div className="bnct_logs-container">
      <h3 className="bnct_section-title">{isProjectBudget ? 'Budget Logs' : 'Bank Logs'}</h3>
      
      {/* # Logs Filter */}
      <div className="bnct_logs-filter">
        <div className="bnct_logs-filter-buttons">
          <button 
            className={logType === 'all' ? 'active' : ''} 
            onClick={() => setLogType('all')}
          >
            All Logs
          </button>
            <button 
            className={logType === 'payment' ? 'active' : ''} 
            onClick={() => setLogType('payment')}
            >
            {isProjectBudget ? 'Budget Allocations' : 'Payment Logs'}
            </button>
                <button 
            className={logType === 'expense' ? 'active' : ''} 
            onClick={() => setLogType('expense')}
                >
            {isProjectBudget ? 'Expense Logs' : 'Salary Logs'}
                </button>
        </div>
        
        <div className="bnct_logs-search">
          <input
            type="text"
            placeholder="Search logs..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button 
            className="bnct_logs-sort-btn"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>
      
      {/* # Logs Table */}
        <div className="bnct_logs-table-container">
          <table className="bnct_logs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Details</th>
              <th>Amount (EGP)</th>
              {!isProjectBudget && logType !== 'expense' && (
                <th>Total Amount (EGP)</th>
              )}
              </tr>
            </thead>
            <tbody>
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log) => (
                <tr key={log.id} className={`bnct_log-${log.type}`}>
                  <td>{formatDate(log.date)}</td>
                  <td>{log.type === 'payment' ? 
                    (isProjectBudget ? 'Budget Allocation' : 'Payment') : 
                    (isProjectBudget ? 'Expense' : 'Salary')}
                  </td>
                  <td>
                    {log.type === 'payment' ? (
                      isProjectBudget ? (
                        <>
                          <strong>Product:</strong> {formatProductName(log.product)}<br />
                          <strong>User:</strong> {log.user || 'Unknown'}
                        </>
                      ) : (
                        <>
                          <strong>Product:</strong> {formatProductName(log.product)}<br />
                          <strong>User:</strong> {log.user || 'Unknown'}<br />
                          <strong>Share:</strong> {log.percentage}%
                        </>
                      )
                    ) : (
                      isProjectBudget ? (
                        <>
                          <strong>Category:</strong> {log.category || 'Uncategorized'}<br />
                          <strong>Details:</strong> {log.details || 'No details provided'}
                        </>
                      ) : (
                        <>
                          <strong>Notes:</strong> {log.notes || 'No notes provided'}
                        </>
                      )
                    )}
                  </td>
                  <td className="bnct_log-amount">
                    E£ {log.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  {!isProjectBudget && log.type === 'payment' && (
                    <td className="bnct_log-amount">
                      E£ {(log.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isProjectBudget || logType === 'expense' ? 4 : 5} className="bnct_no-logs">
                  No logs found matching your criteria
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      
      {/* # Logs Summary */}
      <div className="bnct_logs-summary">
            <div className="bnct_logs-summary-card">
          <h4>Net Revenues (EGP)</h4>
              <p className="bnct_logs-summary-value">
            {paymentLogs.reduce((sum, log) => sum + (log.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          
              <div className="bnct_logs-summary-card">
          <h4>Total Salary (EGP)</h4>
                <p className="bnct_logs-summary-value">
            {expenseLogs.reduce((sum, log) => sum + (log.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className="bnct_logs-summary-card">
          <h4>Available Cash (EGP)</h4>
                <p className="bnct_logs-summary-value">
            {(
              paymentLogs.reduce((sum, log) => sum + (log.amount || 0), 0) - 
              expenseLogs.reduce((sum, log) => sum + (log.amount || 0), 0)
            ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
          
          <div className="bnct_logs-summary-card">
          <h4>Total Transactions</h4>
            <p className="bnct_logs-summary-value">
            {paymentLogs.length + expenseLogs.length}
            </p>
        </div>
      </div>
    </div>
  );
  
  // # Helper function to get filtered logs
  function getFilteredLogs() {
    // # Combine logs based on filter
    let logs = [];
    
    if (logType === 'all' || logType === 'payment') {
      logs = [...logs, ...paymentLogs.map(log => ({ ...log, type: 'payment' }))];
    }
    
    if (logType === 'all' || logType === 'expense') {
      logs = [...logs, ...expenseLogs.map(log => ({ ...log, type: 'expense' }))];
    }
    
    // # Apply search filter
    if (searchTerm) {
      logs = logs.filter(log => {
        const searchLower = searchTerm.toLowerCase();
        
        // # Search in different fields based on log type
        if (log.type === 'payment') {
          return (
            (log.product && log.product.toLowerCase().includes(searchLower)) ||
            (log.user && log.user.toLowerCase().includes(searchLower))
          );
        } else {
          return (
            (log.category && log.category.toLowerCase().includes(searchLower)) ||
            (log.details && log.details.toLowerCase().includes(searchLower)) ||
            (log.notes && log.notes.toLowerCase().includes(searchLower))
          );
        }
      });
    }
    
    // # Sort logs by date
    logs.sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    
    return logs;
  }
};

// # Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// # Helper function to format product names for better display
const formatProductName = (product) => {
  if (!product) return 'Unknown Product';
  
  // # Replace underscores with spaces and capitalize words
  let formattedName = product.replace(/_/g, ' ');
  
  // # Handle specific product types
  if (formattedName.includes('step1 library')) {
    return 'Step 1 Library Subscription';
  } else if (formattedName.includes('step2 library')) {
    return 'Step 2 Library Subscription';
  } else if (formattedName.includes('step1 premium')) {
    return 'Step 1 Premium Subscription';
  } else if (formattedName.includes('step2 premium')) {
    return 'Step 2 Premium Subscription';
  } else if (formattedName.includes('step1 mentorship')) {
    return 'Step 1 Mentorship';
  } else if (formattedName.includes('step2 mentorship')) {
    return 'Step 2 Mentorship';
  } else if (formattedName.includes('OET')) {
    return 'OET Mentorship';
  } else if (formattedName.includes('US VISA')) {
    return 'US VISA Counselling';
  }
  
  // # Capitalize first letter of each word
  return formattedName.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default BankLogs; 